import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

// 345: filtr jednotné lišty (vzor Linear). Pole má víc hodnot a „je / není"; stav je v adrese
// (?stav=V realizaci,Nabídka · ?stav=!Dokončeno), ať jde odkaz poslat a Zpět vrátí filtr.
//   groups = [{ key, label, icon, options:[{value,label,photo?}], match(item, value) -> bool }]
//   noNeg: true = bez „není" (server umí jen „je"); counts = počty u hodnot, když filtruje server;
//   labelOf(value) = popisek vybrané hodnoty, která v aktuální nabídce chybí (server vrací jen dnešní autory…).
//   external = { vals, labels, open(), clear(), toggle(v) } — pole s vlastním oknem a stavem mimo adresu
//   (Úkoly › Kdo: výběr lidí s uloženými partami). V pilulkách je jako ostatní. Ve Filtru otevře své okno,
//   nebo s render(ctx) vykreslí vlastní krok přímo ve Filtru (359; ctx = { mobile, onDone, onBack }).
//   Pole s datem: { key, label, icon, type: "date" | "range", format(raw) -> text } — hodnota je
//   „2026-10-28" nebo „2026-10-01..2026-10-28"; filtrování dělá stránka sama (často na serveru).
// `items` = položky po rychlé záložce; null = stránka filtruje na serveru (hook jen drží stav).
// Čárka je oddělovač — v hodnotě (např. kategorie od uživatele) se zapíše jako %2C.
const enc = (v) => String(v).replace(/%/g, "%25").replace(/,/g, "%2C");
const dec = (v) => v.replace(/%2C/gi, ",").replace(/%25/g, "%");

export function parseFilter(raw) {
  if (!raw) return null;
  const neg = raw.startsWith("!");
  const vals = (neg ? raw.slice(1) : raw).split(",").filter(Boolean).map(dec);
  return vals.length ? { vals, neg } : null;
}

export function useListFilters(items, { groups = [], entity = { one: "položka", few: "položky", many: "položek" }, search, searchTerm = "" } = {}) {
  const [params, setParams] = useSearchParams();
  const state = {};
  groups.forEach((g) => {
    if (g.external) { if (g.external.vals?.length) state[g.key] = { vals: g.external.vals, neg: false }; return; }
    const f = parseFilter(params.get(g.key)); if (f) state[g.key] = g.noNeg || g.type ? { ...f, neg: false } : f;
  });

  const write = (key, f) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (!f || !f.vals.length) n.delete(key); else n.set(key, (f.neg ? "!" : "") + f.vals.map(enc).join(","));
    return n;
  }, { replace: true });
  const groupOf = (key) => groups.find((g) => g.key === key);
  const toggleValue = (key, value) => {
    if (groupOf(key)?.external) { groupOf(key).external.toggle?.(value); return; }
    const cur = parseFilter(params.get(key)) || { vals: [], neg: false };
    const vals = cur.vals.includes(value) ? cur.vals.filter((v) => v !== value) : [...cur.vals, value];
    write(key, { vals, neg: cur.neg });
  };
  const setRaw = (key, raw) => write(key, raw ? { vals: [raw], neg: false } : null);   // pole s datem
  const toggleNeg = (key) => { const cur = parseFilter(params.get(key)); if (cur && !groupOf(key)?.noNeg) write(key, { ...cur, neg: !cur.neg }); };
  const clearOne = (key) => (groupOf(key)?.external ? groupOf(key).external.clear() : write(key, null));
  const clear = () => {
    groups.forEach((g) => { if (g.external && g.external.vals?.length) g.external.clear(); });
    setParams((p) => { const n = new URLSearchParams(p); groups.forEach((g) => { if (!g.external) n.delete(g.key); }); return n; }, { replace: true });
  };
  const isOn = (key, value) => !!state[key]?.vals.includes(value);

  const term = searchTerm.trim().toLowerCase();
  const plural = (n) => (n === 1 ? entity.one : n >= 2 && n <= 4 ? entity.few : entity.many);
  const passes = (it, g, f) => { const has = f.vals.some((v) => g.match(it, v)); return f.neg ? !has : has; };
  const key = JSON.stringify(state);

  const { visible, counts } = useMemo(() => {
    // filtr na serveru: počty u hodnot dodá stránka (group.counts = { hodnota: počet })
    if (!items) return { visible: null, counts: Object.fromEntries(groups.filter((g) => g.counts).map((g) => [g.key, g.counts])) };
    const listGroups = groups.filter((g) => g.match && !g.external && state[g.key]);   // external filtruje stránka sama
    const passSearch = (it) => !term || (search ? search(it) : "").toLowerCase().includes(term);
    const passAll = (it, except) => listGroups.every((g) => g.key === except || passes(it, g, state[g.key]));
    const vis = items.filter((it) => passSearch(it) && passAll(it));
    const cnt = {};
    groups.filter((g) => g.match && !g.external).forEach((g) => {
      const base = items.filter((it) => passSearch(it) && passAll(it, g.key));
      cnt[g.key] = Object.fromEntries((g.options || []).map((o) => [o.value, base.filter((it) => g.match(it, o.value)).length]));
    });
    return { visible: vis, counts: cnt };
  }, [items, key, term, groups]); // eslint-disable-line react-hooks/exhaustive-deps

  const filters = groups.filter((g) => state[g.key]).map((g) => {
    const f = state[g.key];
    const labels = g.external?.labels || (g.type ? [g.format ? g.format(f.vals[0]) : f.vals[0]] : f.vals.map((v) => (g.options || []).find((o) => o.value === v)?.label || g.labelOf?.(v) || v));
    return { key: g.key, label: g.label, icon: g.icon, type: g.type, noNeg: !!(g.noNeg || g.external), external: !!g.external, neg: f.neg, vals: f.vals, labels };
  });

  return {
    groups, filters, state, visible, counts, plural, toggleValue, toggleNeg, setRaw, clearOne, clear, isOn, groupOf,
    filterCount: filters.length,
    resultCount: visible ? visible.length : null,
  };
}
